/* PrimeStyle — custom design request sender (2026-09-22)
   -------------------------------------------------------------------------
   Until today the five category pages, the custom-rings page and
   design-customize mounted the request engine (ps-cust26-form.js) with NO
   onSubmit. The engine's own submit() is a no-op in that case, so the shopper
   saw "Your idea is with our design team" and the request went nowhere: no
   email, no Klaviyo event, no record of any kind. This file is the missing
   transport, shared by every custom page so there is one code path.

   Two routes, in order of preference:

   1. "Attachment endpoint" (theme setting custom_form_endpoint, a Google Apps
      Script /exec URL running tools/ps-design-request-mailer.gs). This is the
      ONLY route that can carry the shopper's photos: it takes them as base64
      fields and the script re-attaches them to the email. Shopify's contact
      form has no file transport at all, which is why photos have only ever
      arrived as a list of filenames.

   2. Shopify's contact form, when no endpoint is configured. The store still
      gets the request; the body says plainly that photos were left behind so
      nobody assumes the shopper sent nothing.

   The photo sent is the shopper's ORIGINAL file, not the 480px preview the
   engine keeps for its thumbnails and localStorage.

   2026-10-12 (lead tracking): onSubmit returns a Promise the engine waits
   on, so "done" only shows for a request that actually left. Delivery,
   attribution and the Meta/GA4 lead live in ps-cust26-lead.js; the lead
   fires only on a confirmed route (or on the contact form's return trip). */
(function () {
  'use strict';

  var MOUNT = document.getElementById('ps26-send');
  if (!MOUNT) return;

  var PAGE = MOUNT.getAttribute('data-page') || 'Custom design';
  var ENDPOINT = (MOUNT.getAttribute('data-endpoint') || '').trim();
  var FORM = document.getElementById('ps26ContactForm');

  /* Apps Script accepts a large POST, but a phone photo set can be 40MB+ and a
     silent truncation would be worse than a smaller picture. Over the cap we
     fall back to the engine's own downscaled copy, which is always present. */
  var PER_IMAGE_MAX = 7 * 1024 * 1024;
  var TOTAL_MAX = 20 * 1024 * 1024;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  /* ---- the body the store reads, identical in shape to the hub's ---- */
  function bodyLines(p, photoNote) {
    var st = p.state || {};
    var lines = ['New custom design request via ' + PAGE + '.', 'Reference: ' + p.ref];
    if (st.idea) lines.push('Idea: ' + st.idea);
    if (photoNote) lines.push(photoNote);
    Object.keys(st.details || {}).forEach(function (k) {
      var v = (st.details[k] || []).filter(function (x) { return x !== 'Not sure yet'; });
      if (v.length) lines.push(k + ': ' + v.join(', '));
    });
    if (st.budget) lines.push('Budget: ' + st.budget);
    if (st.occasion) lines.push('Occasion: ' + st.occasion);
    if (st.date) lines.push('Ready by: ' + st.date);
    lines.push('Contact: ' + (st.name || '') + ' / ' + (st.email || '') +
      (st.phone ? ' / ' + st.phone : '') + ' / prefers ' + (st.contact || 'Email'));
    lines.push('Page: ' + location.href.split(/[?#]/)[0]);
    return lines.join('\n');
  }

  /* ---- read the shopper's ORIGINAL files back as base64 ---- */
  function readOriginals(images, done) {
    var out = [], total = 0, pending = 0, finished = false;
    if (!images || !images.length) return done(out);

    function settle() {
      if (!finished && pending === 0) { finished = true; done(out); }
    }
    /* A stuck FileReader must never strand the request. */
    setTimeout(function () { if (!finished) { finished = true; done(out); } }, 15000);

    images.forEach(function (im, i) {
      var name = im.name || ('photo-' + (i + 1) + '.jpg');
      var f = im.file;
      if (f && f.size && f.size <= PER_IMAGE_MAX && (total + f.size) <= TOTAL_MAX) {
        pending++;
        total += f.size;
        var r = new FileReader();
        r.onload = function () { out.push({ name: name, data: String(r.result) }); pending--; settle(); };
        r.onerror = function () {
          if (im.data) out.push({ name: name, data: im.data });
          pending--; settle();
        };
        try { r.readAsDataURL(f); } catch (e) { pending--; settle(); }
      } else if (im.data) {
        /* no File handle (restored from a saved draft) or too large: the
           engine's downscaled copy still beats sending nothing */
        out.push({ name: name, data: im.data });
      }
    });
    settle();
  }

  var L = window.PSCustLead || null;
  var FORM_ID = L ? L.formId() : 'custom';

  /* ---- route 1: the attachment endpoint's fields ---- */
  function endpointParams(p, files) {
    var st = p.state || {};
    var note = files.length
      ? 'Photos: ' + files.length + ' attached to this email (' +
        files.map(function (f) { return f.name; }).join(', ') + ')'
      : '';
    var params = new URLSearchParams({
      name: st.name || '', email: st.email || '', phone: st.phone || '',
      page: PAGE, reference: p.ref || '', body: bodyLines(p, note),
      image_count: String(files.length)
    });
    /* event_id and the store-only attribution block. NEVER inside body: the
       mailer echoes body into the shopper's own confirmation. */
    if (p.eventId) params.append('event_id', p.eventId);
    if (L) params.append('internal', L.internalBlock(p.eventId));
    files.forEach(function (f, i) {
      params.append('image' + i + '_name', f.name);
      params.append('image' + i + '_data', f.data);
    });
    return params;
  }

  /* ---- route 2: Shopify's contact form ---- */
  function fillContactForm(p, files) {
    return function (form) {
      var st = p.state || {};
      var note = files.length
        ? 'Photos: ' + files.length + ' added on the site (' +
          files.map(function (f) { return f.name; }).join(', ') +
          '). NOT attached here: Shopify\'s contact form cannot carry files. ' +
          'Reply to the customer and ask them to send the photos.'
        : '';
      var set = function (n, v) { var e = form.querySelector('[name="' + n + '"]'); if (e) e.value = v; };
      set('contact[email]', st.email || '');
      set('contact[name]', st.name || 'Website visitor');
      set('contact[phone]', st.phone || '');
      set('contact[body]', bodyLines(p, note));
    };
  }

  /* ---- Klaviyo: who, at SEND; the request event, once delivered ---- */
  function identify(st) {
    try {
      if (!st.email) return;
      window.klaviyo = window.klaviyo || [];
      var who = { email: st.email };
      if (st.name) who.first_name = String(st.name).split(' ')[0];
      if (st.phone) who.phone_number = st.phone;
      window.klaviyo.push(['identify', who]);
      if (L && L.identified) L.identified();
    } catch (e) {}
  }
  function klaviyo(p, files, route) {
    try {
      var st = p.state || {};
      if (!st.email) return;
      window.klaviyo = window.klaviyo || [];
      var props = {
        page: PAGE,
        form: (window.CUST26 && window.CUST26.category) || 'category',
        reference: p.ref,
        photos: files.length,
        summary: bodyLines(p, ''),
        first_name: st.name ? String(st.name).split(' ')[0] : '',
        phone: st.phone || '',
        source_url: location.href.split(/[?#]/)[0],
        event_id: p.eventId || '',
        delivery_route: route
      };
      if (L) { var a = L.klaviyoProps(); Object.keys(a).forEach(function (k) { props[k] = a[k]; }); }
      /* not in the same moment as the identify, or Klaviyo drops it */
      var wait = (L && L.trackDelay) ? L.trackDelay() : 0;
      if (wait) setTimeout(function () { window.klaviyo.push(['track', 'Custom Design Request', props]); }, wait);
      else window.klaviyo.push(['track', 'Custom Design Request', props]);
      return wait;
    } catch (e) {}
  }

  /* the non-personal facts every lead event carries */
  function leadInfo(p) {
    var st = p.state || {};
    return {
      ref: p.ref, eventId: p.eventId,
      pieceType: L ? L.pieceType(p.category) : '',
      budget: st.budget || '', timeline: L ? L.timeline(st) : '',
      form: FORM_ID
    };
  }

  /* ---- before 2026-10-12: fire and forget (kept for a missing lead script) ---- */
  function legacySend(p, files) {
    var params = endpointParams(p, files);
    var fill = fillContactForm(p, files);
    var viaForm = function () {
      if (!FORM) return;
      fill(FORM);
      FORM.requestSubmit ? FORM.requestSubmit() : FORM.submit();
    };
    klaviyo(p, files, 'legacy');
    if (!ENDPOINT) { viaForm(); return; }
    var payload = params.toString();
    var init = { method: 'POST', mode: 'no-cors',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' }, body: payload };
    if (payload.length < 60000) init.keepalive = true;
    fetch(ENDPOINT, init)['catch'](viaForm);
  }

  function onSubmit(p) {
    var st = (p && p.state) || {};
    var images = (st.images || []).concat(st.matchImages || []);
    identify(st);
    if (!L) {
      readOriginals(images, function (files) { legacySend(p, files); });
      return undefined;
    }
    return new Promise(function (resolve) {
      readOriginals(images, function (files) {
        var info = leadInfo(p);
        L.deliver({
          endpoint: ENDPOINT,
          params: endpointParams(p, files),
          form: FORM,
          fill: fillContactForm(p, files),
          posted: {
            ref: p.ref, eventId: p.eventId, pieceType: info.pieceType, budget: info.budget,
            timeline: info.timeline, form: info.form, draftKey: p.draftKey || '',
            email: st.email || ''
          },
          /* the contact form is a page navigation: record the request first */
          onRouted: function (route) { return klaviyo(p, files, route); }
        }).then(function (res) {
          if (res.ok) {
            klaviyo(p, files, res.route);
            info.route = res.route;
            L.lead(info);
          }
          resolve(res);
        });
      });
    });
  }

  var CFG = window.CUST26 = window.CUST26 || {};
  if (typeof CFG.onSubmit !== 'function') CFG.onSubmit = onSubmit;

  /* ---- back from Shopify with contact_posted=true: say so again ---- */
  (function restore() {
    var flag = document.getElementById('ps26Posted');
    var was = (flag && flag.getAttribute('data-posted') === '1') ||
      /[?&]contact_posted=true/.test(location.search);
    if (!was) return;
    /* the lead is reported here, once: the send left through the contact form */
    var last = (window.PSCustLead && window.PSCustLead.restorePosted()) || null;
    if (!last) {
      try {
        last = JSON.parse(localStorage.getItem('ps-custom:last-sent') || 'null');
        localStorage.removeItem('ps-custom:last-sent');
      } catch (e) {}
    }
    try {
      history.replaceState(null, '', location.pathname +
        location.search.replace(/[?&]contact_posted=true/, '').replace(/^&/, '?'));
    } catch (e) {}
    var root = document.getElementById('cf-root') || document.querySelector('.cf-root, [data-cf-phase]');
    if (!root) return;
    var d = document.createElement('div');
    d.className = 'cf-done';
    d.innerHTML = '<div class="mark"><svg viewBox="0 0 24 24" fill="none" stroke-width="2" aria-hidden="true">' +
      '<path d="M4 12.5l5 5L20 6.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div>' +
      '<h3 class="cf-title" tabindex="-1">Your idea is with our design team.</h3>' +
      (last && last.ref ? '<p class="ref">Reference <b>' + esc(last.ref) + '</b>' +
        (last.email ? ' &middot; a designer replies to ' + esc(last.email) : '') + '</p>' : '') +
      '<div class="cf-next-steps">' +
      '<div><strong>A designer reads your request</strong><p>Within 1 to 2 business days. A real person, thinking about your piece.</p></div>' +
      '<div><strong>Your free 3D design arrives</strong><p>A photoreal render of your exact piece, usually within 2 business days. Free, no obligation.</p></div>' +
      '<div><strong>You approve the design</strong><p>We refine round after round until you say it is right. Nothing is made before that.</p></div>' +
      '<div><strong>We craft it in our US factory</strong><p>Made start to finish in 7 to 9 days in our own US factory, then shipped free and insured within the US.</p></div>' +
      '</div>';
    root.innerHTML = '';
    root.appendChild(d);
    try { root.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
  })();
})();
